/**
 * Tier identity — the single frontend source of truth for tier slugs and labels.
 *
 * Mirrors App\Enums\UserTier on the backend. The string *values* (anonymouse,
 * account, pro) are frozen: they are DB columns (tier, current_tier,
 * tier_at_creation) and the /api/me contract. The *labels* are display-only and
 * standardized to Guest / Free / Pro across all UI so the three vocabularies
 * (DB value, code constant, on-screen label) never drift apart again.
 *
 * Note the deliberate value↔label mismatches: Free's frozen value is `account`, and the guest
 * tier's is `anonymouse` while it is shown as "Guest". Always reference TIER.* / tierLabel()
 * rather than raw string literals so a future rename breaks loudly here, in one place.
 */

export const TIER = {
  ANONYMOUSE: 'anonymouse',
  FREE: 'account',
  PRO: 'pro',
} as const;

export type TierValue = typeof TIER[keyof typeof TIER];

/**
 * What a user READS. Deliberately not the stored value — see `TIER`, where Free is `account` and
 * the guest tier is `anonymouse`. The identifiers keep the stored spelling so they match the DB,
 * `config/tiers.php` and the `/api/me` payload; only this map is the product's vocabulary.
 */
export const TIER_LABEL: Record<TierValue, string> = {
  [TIER.ANONYMOUSE]: 'Guest',
  [TIER.FREE]: 'Free',
  [TIER.PRO]: 'Pro',
};

/** Display label for a tier value; falls back to the guest tier for null/unknown (fail-closed). */
export function tierLabel(tier: TierValue | null | undefined): string {
  if (!tier) {
    return TIER_LABEL[TIER.ANONYMOUSE];
  }

  return TIER_LABEL[tier] ?? tier;
}
